import Handlebars from 'handlebars';
import { sum } from 'lodash/fp';

import { pathIsNormative } from 'dashboard/lib/workflows/method-chain';

interface FlattenedPosition {
  start: number;
  end: number;
}
interface HandlebarsStatement {
  content: string;
  variableName: string | null;
  position: FlattenedPosition;
}

const CALL_HELPER = 'call';

const isMustacheStatement = (
  statement: hbs.AST.Statement,
): statement is hbs.AST.MustacheStatement => statement.type === 'MustacheStatement';

const isCallHelper = (statement: hbs.AST.MustacheStatement): boolean =>
  isPathExpression(statement.path) &&
  statement.path.original === CALL_HELPER &&
  statement.params.length === 1 &&
  statement.params[0].type === 'StringLiteral';

const isContentStatement = (statement: hbs.AST.Statement): statement is hbs.AST.ContentStatement =>
  statement.type === 'ContentStatement';

const isPathExpression = (
  path: hbs.AST.MustacheStatement['path'],
): path is hbs.AST.PathExpression => path.type === 'PathExpression';

const getFlattenedPosition = (
  multilineText: string,
  location: hbs.AST.SourceLocation,
): FlattenedPosition => {
  const lines = multilineText.split('\n').map((line) => line.length + 1);
  const start = sum(lines.slice(0, location.start.line - 1)) + location.start.column;
  const end = sum(lines.slice(0, location.end.line - 1)) + location.end.column - 1;

  return { start, end };
};

/**
 * Returns HandlebarsStatement[] if the text is valid as our expectation for Handlebars.
 * We only support simple expressions like "before {{object.val}} after {{object.end}}"
 * Don't support any fancy Handlebars feature: helpers like "{{func value}}", nesting like "{{#each people}}...{{/each}}"
 * When invalid text is given, throws an exception
 * So this function should be used with try-catch to check whether given handlebars string is legit
 *
 * @param {string} text - a text which may have handlebars expressions
 * @returns {HandlebarsStatement[]} a list of splitted text block with metadata (found name, position)
 * For example, "This is a {{variable}}" will be parsed as:
 * [{
 *  content: 'This is a ',
 *  variableName: null,
 *  position: { start: 0, end: 9 }
 * },
 * {
 *  content: '{{variable}},
 *  variableName: 'variable',
 *  position: { start: 10, end: 21 }
 * }]
 */
export const parseHandlebarsText = (text: string): HandlebarsStatement[] => {
  // (evan.qi) backend handlebars library will raise a parsing error when
  // {} is present with other text
  if (text !== '{}' && text.includes('{}')) {
    throw 'Text cannot contain handlebars and {}';
  }

  const ast = Handlebars.parseWithoutProcessing(text);

  return ast.body.map((statement) => {
    // handlebars module gives row/col based location, so converting to a position of string
    const position = getFlattenedPosition(text, statement.loc);
    if (isMustacheStatement(statement)) {
      if (isCallHelper(statement)) {
        return {
          content: text.slice(position.start, position.end + 1),
          variableName: (statement.params[0] as hbs.AST.StringLiteral).original,
          position,
        };
      }
      // "call" is the only helper we accept
      if (statement.params.length > 0 || !isPathExpression(statement.path)) {
        throw 'Unrecognized handlebars helper';
      }
      return {
        content: text.slice(position.start, position.end + 1),
        variableName: statement.path.original,
        position,
      };
    } else if (isContentStatement(statement)) {
      return {
        content: text.slice(position.start, position.end + 1),
        variableName: null,
        position,
      };
    } else {
      throw "We don't support Handlebars helper";
    }
  });
};

/**
 * Check if given string is valid as text which may have Handlebars expressions
 *
 * @param {string} text a text which may have Handlebars expressions
 * @returns {boolean} - whether given text is valid as text with Handlebars expressions
 */
export const isValidHandlebarsText = (text: string): boolean => {
  try {
    parseHandlebarsText(text);
    return true;
  } catch {
    return false;
  }
};

/**
 * Find Handlebars Mustaches from a text
 *
 * @param {string} text - a text which may have Handlebars expressions
 * @returns {string[]} a list of Handlebars Mustaches (only PathExpressions) that are found on given text
 */
export const getHandlebars = (text: string): string[] => {
  try {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return parseHandlebarsText(text)
      .map((statement) => statement.variableName)
      .filter(Boolean);
  } catch {
    return [];
  }
};

export const validateResolvedJson = (jsonString: string): boolean => {
  try {
    let blanked = '';
    const statements = parseHandlebarsText(jsonString);
    // Since handlebars.js doesn't provide a way to hook "missing property",
    // https://github.com/handlebars-lang/handlebars.js/issues/1435
    // This code is manually replacing all Handlebar Mustaches with '{}'
    statements.forEach((statement) => {
      blanked += statement.variableName == null ? statement.content : '{}';
    });
    JSON.parse(blanked);
    return true;
  } catch {
    return false;
  }
};

/**
 * Formats each handlebar word using the provided formatter
 *
 * @param value
 * @param formatter
 * @returns formatted value
 */
export const formatHandlebarValue = (value: string, formatter: (val: string) => string) => {
  const handlebarWords = getHandlebars(value);
  return handlebarWords.reduce((displayValue, word) => {
    // need to escape special characters before generating the regex
    const pattern = new RegExp(`{{${word}}}`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    const callString = `{{${callHelper(word, true)}}}`;
    return displayValue
      .replace(pattern, `{{${formatter(word)}}}`)
      .replace(callString, `{{${callHelper(formatter(word), true)}}}`);
  }, value);
};

export const callHelper = (value: string, force = false): string => {
  if (!force && pathIsNormative(value)) {
    return value;
  }
  return `${CALL_HELPER} "${value}"`;
};
